import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { api } from "../lib/api";
import { fmtDate, money, customerName } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { JobDialog } from "../components/jobs/JobDialog";

const FILTERS = { upcoming: "Upcoming", completed: "Completed", cancelled: "Cancelled", all: "All" };
const today = format(new Date(), "yyyy-MM-dd");

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [installer, setInstaller] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/jobs").then((r) => setJobs(r.data)), []);
  useEffect(() => { load(); api.get("/installers").then((r) => setInstallers(r.data)); }, [load]);

  const shown = jobs.filter((j) =>
    (filter === "all" || (filter === "upcoming" ? ["scheduled", "in_progress"].includes(j.status) : j.status === filter)) &&
    (!installer || (installer === "none" ? !j.installer_id : j.installer_id === installer)));

  return (
    <div>
      <PageHeader eyebrow="Installs" title="Jobs">
        <select data-testid="jobs-installer-filter" value={installer} onChange={(e) => setInstaller(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
          <option value="">All installers</option><option value="none">Unassigned</option>
          {installers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </PageHeader>
      <div className="mb-4 flex gap-1">
        {Object.entries(FILTERS).map(([k, v]) => (
          <button key={k} data-testid={`jobs-filter-${k}`} onClick={() => setFilter(k)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 ring-1 ring-zinc-200 hover:ring-zinc-400"}`}>{v}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
        <table className="w-full min-w-[800px] text-sm" data-testid="jobs-table">
          <thead className="border-b border-zinc-200 bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            <tr><th className="px-5 py-3">Install date</th><th className="px-3">Job</th><th className="px-3">Customer / site</th><th className="px-3">Installer</th><th className="px-3">Status</th><th className="px-3">Quote</th><th className="px-5 text-right">Value inc GST</th></tr>
          </thead>
          <tbody>
            {shown.map((j) => {
              const overdue = ["scheduled", "in_progress"].includes(j.status) && j.scheduled_date < today;
              return (
                <tr key={j.id} data-testid={`job-row-${j.number}`} onClick={() => setEditing(j)} className="cursor-pointer border-b border-zinc-100 last:border-0 hover:bg-slate-50">
                  <td className="px-5 py-3"><div className={`font-mono font-semibold ${overdue ? "text-red-600" : ""}`}>{fmtDate(j.scheduled_date)}</div><div className="text-xs text-zinc-400">{[j.scheduled_time, overdue && "overdue"].filter(Boolean).join(" · ") || "—"}</div></td>
                  <td className="px-3"><div className="font-mono font-semibold">{j.number}</div><div className="text-xs text-zinc-500">{j.title}</div></td>
                  <td className="px-3"><div className="font-medium">{customerName(j.customer)}</div><div className="text-xs text-zinc-500">{j.site_address}</div></td>
                  <td className="px-3">{j.installer_name || <span className="text-zinc-400">Unassigned</span>}</td>
                  <td className="px-3"><StatusBadge status={j.status} /></td>
                  <td className="px-3"><Link data-testid={`job-quote-link-${j.number}`} to={`/quotes/${j.quote_id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-red-600 hover:underline">{j.quote_number}</Link></td>
                  <td className="px-5 text-right font-mono font-semibold">{money(j.total)}</td>
                </tr>
              );
            })}
            {!shown.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-400">No jobs here. Approve a quote and click "Create job" to schedule one.</td></tr>}
          </tbody>
        </table>
      </div>
      <JobDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} job={editing} onSaved={load} />
    </div>
  );
}
